import React, { useState } from 'react';
import { Dialog, Button, Input, Label, Select } from '@repo/ui';
import { Icon } from '@repo/ui';
import { showToast } from '@repo/ui';
import { juniperQueryApi } from '../api';

interface WebhookSimulationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface SimulateWebhookRequest {
  billNumber: string;
  billType: 'operational' | 'trust';
  eventType: 'created' | 'updated' | 'deleted' | 'voided';
  reason?: string;
  stateOverride?: 'draft' | 'awaiting_approval' | 'awaiting_payment' | 'paid' | 'void';
}

interface SimulateWebhookResponse {
  success: boolean;
  eventId: string;
  sqsMessageId: string;
  webhookId: number;
  billId: number;
  message: string;
}

export const WebhookSimulationModal: React.FC<WebhookSimulationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<SimulateWebhookRequest>({
    billNumber: '',
    billType: 'operational',
    eventType: 'updated',
    reason: '',
    stateOverride: undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.billNumber.trim()) {
      showToast.error('Bill number is required');
      return;
    }

    setIsLoading(true);

    try {
      const response = await juniperQueryApi.simulateWebhookEvent(formData);

      showToast.success(
        `✅ Missing event submitted successfully! Event ID: ${response.eventId.substring(0, 8)}...`
      );

      // Reset form
      setFormData({
        billNumber: '',
        billType: 'operational',
        eventType: 'updated',
        reason: '',
        stateOverride: undefined,
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      console.error('Webhook request failed:', error);

      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to simulate webhook';
      showToast.error(`❌ Missing event submission failed: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="md">
      <div className="flex items-center space-x-3 p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
            {/* TODO: add a double arrow icon */}
            <Icon name="HiExclamation" size="md" className="text-yellow-600 dark:text-yellow-400" />
          </div>
        </div>
        <div>
          <h2 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-white">
            Request Missing Clio Event
          </h2>
          <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
            Manually request a missing Clio bill event to be processed
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-6">
          {/* Bill Number */}
          <div>
            <Label htmlFor="billNumber">
              Bill Number <span className="text-red-500">*</span>
            </Label>
            <Input
              id="billNumber"
              style={{ width: '100%' }}
              value={formData.billNumber}
              onChange={e => setFormData(prev => ({ ...prev, billNumber: e.target.value }))}
              placeholder="Enter bill number (e.g., 2024)"
              disabled={isLoading}
              required
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Enter the exact bill number as it appears in Clio
            </p>
          </div>

          {/* Bill Type */}
          <div>
            <Label htmlFor="billType">
              Bill Type <span className="text-red-500">*</span>
            </Label>
            <Select
              options={[
                { value: 'operational', label: 'Operational Bill' },
                { value: 'trust', label: 'Trust Request' },
              ]}
              value={formData.billType}
              onChange={value =>
                setFormData(prev => ({ ...prev, billType: value as 'operational' | 'trust' }))
              }
              disabled={isLoading}
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Select whether this is an operational bill or trust request
            </p>
          </div>

          {/* Event Type */}
          <div>
            <Label htmlFor="eventType">
              Event Type <span className="text-red-500">*</span>
            </Label>
            <Select
              options={[
                { value: 'created', label: 'Created' },
                { value: 'updated', label: 'Updated' },
                { value: 'deleted', label: 'Deleted' },
                { value: 'voided', label: 'Voided' },
              ]}
              value={formData.eventType}
              onChange={value =>
                setFormData(prev => ({
                  ...prev,
                  eventType: value as 'created' | 'updated' | 'deleted' | 'voided',
                }))
              }
              disabled={isLoading}
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Select the type of event that should have been triggered
            </p>
          </div>

          {/* State Override (Optional) */}
          <div>
            <Label htmlFor="stateOverride">State Override (Optional)</Label>
            <Select
              options={[
                { value: '', label: 'Use actual bill state from Clio' },
                { value: 'draft', label: 'Draft' },
                { value: 'awaiting_approval', label: 'Awaiting Approval' },
                { value: 'awaiting_payment', label: 'Awaiting Payment' },
                { value: 'paid', label: 'Paid' },
                { value: 'void', label: 'Void' },
              ]}
              value={formData.stateOverride || ''}
              onChange={value =>
                setFormData(prev => ({
                  ...prev,
                  stateOverride: value === '' ? undefined : value as any,
                }))
              }
              disabled={isLoading}
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Override the bill state in the webhook payload (for testing different processing paths)
            </p>
          </div>

          {/* Reason (Optional) */}
          <div>
            <Label htmlFor="reason">Reason (Optional)</Label>
            <Input
              style={{ width: '100%' }}
              id="reason"
              value={formData.reason}
              onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="e.g., Missed event - manual trigger required"
              disabled={isLoading}
            />
            <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
              Optional reason for requesting this event
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            disabled={!formData.billNumber.trim()}
          >
            {isLoading ? 'Requesting...' : 'Request Missing Event'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
};
